import { Alert, Box, Divider, FormHelperText, Grid2, Skeleton, Stack, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import SelectYesNo from "components/SelectYesNo";
import IndieBackButton from "components/forms/IndieBackButton";
import IndieContinueButton from "components/forms/IndieContinueButton";
import IndieDesktopDatePicker from "components/forms/IndieDesktopDatePickerV2";
import IndieIBANTextField from "components/forms/IndieIBANTextField";
import IndieNumberField from "components/forms/IndieNumberField";
import IndieSaveButton from "components/forms/IndieSaveButton";
import IndieTextField from "components/forms/IndieTextField";
import CommunityTypeSelector, { communityTypes } from "components/forms/communities/CommunityTypeSelector";
import EntityFormFields from "components/forms/entities/EntityFormFields";
import IndieReceiptMethodsSelect from "components/forms/tariffs/IndieReceiptMethodsSelect";
import PhysicalUnitSelect from "components/markets/PhysicalUnitSelect";
import { ClientsContext } from "contexts/ClientsContext";
import { UserFeedbackContext } from "contexts/UserFeedbackContext";
import { Form, Formik, useFormikContext } from "formik";
import { t } from "i18next";
import _ from "lodash";
import { IndieSimpleTable } from "page-sections/common/IndieSimpleTable";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCommunitiesAPI } from "service/CommunitiesAPIService";
import { validateCPE, validateFiscalNumber, validateIBAN } from "utils/utils";
import * as Yup from "yup";
import CommunityBannerUpload from "./CommunityBannerUpload";
import { IndieDSOIDField, IndieLabelAndValue } from "./NewCommunityDialog";

export const AggregationCommunityForm = ({ handleSuccess, initialData = null }) => {
    const { setAndShowErrorMessage, setAndShowSuccessMessage } = useContext(UserFeedbackContext);
    // FIXME: JG não faz muito sentido usar clientcontext, mas sim agent entity context para ir buscar os agentes
    const { fetchAggregators } = useContext(ClientsContext);
    const communitiesAPI = useCommunitiesAPI();

    const [activeStep, setActiveStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [ownerEntities, setOwnerEntities] = useState([]);
    const [loadingOwnerEntities, setLoadingOwnerEntities] = useState(true);
    const [selectedOwnerEntity, setSelectedOwnerEntity] = useState(null);

    const columns = useMemo(() => [
        { field: "entity.name", label: t("label.table.header.companyName"), align: "left" },
        { field: "entity.fiscal_number", label: t("label.table.header.fiscalNumber"), align: "left" },
        { field: "resp_party.name", label: t("label.table.header.respName"), align: "left" },
    ], []);

    const initialValues = {
        name: "",
        dso_id: "",
        activation_date: null,
        agent_entity: initialData ? initialData.agent_entity : null,
        type: null,
        banner_image: null,
        egac_client: initialData ? {
            fiscal_number: initialData.fiscal_number,
            zip_code: initialData.zip_code,
            address: initialData.address,
            door_number: initialData.door_number,
            apartment_number: initialData.apartment_number,
            locality: initialData.locality,
            district: initialData.district,
            country: initialData.country,
            name: initialData.name,
            email: initialData.email,
            phone: initialData.phone,
            iban: initialData.iban,
            receipt_tax_regime: initialData.receipt_tax_regime,
            receipt_has_open_activity: initialData.receipt_has_open_activity,
            receipt_use_auto_invoicing: initialData.receipt_use_auto_invoicing,
            receipt_method: 1
        } : {
            receipt_method: 1
        }
    };

    const step1ValidationSchema = Yup.object().shape({
        agent_entity: Yup.object().required(t("error.forms.mandatory")),
    });

    const step2ValidationSchema = Yup.object().shape({
        dgeg_fiscal_number: Yup.string().notRequired()
            .matches(/^\d{9}$/, t('error.forms.fiscalNumberDigits'), { excludeEmptyString: true })
            .test('valid-nif', t('error.forms.fiscalNumberInvalid'), (value) => {
                if (!value) return true;
                return validateFiscalNumber(value);
            }),
        dgeg_name: Yup.string().notRequired(),
    });

    const step3ValidationSchema = Yup.object().shape({
        egac_client: Yup.object().shape({
            fiscal_number: Yup.string().required(t("error.forms.mandatory"))
                .matches(/^\d{9}$/, t('error.forms.fiscalNumberDigits'))
                .test('valid-nif', t('error.forms.fiscalNumberInvalid'), (value) => {
                    return validateFiscalNumber(value);
                }),
            zip_code: Yup.string().required(t("error.forms.mandatory")),
            name: Yup.string().required(t("error.forms.mandatory")),
            email: Yup.string().email(t("error.forms.emailFormat")).required(t("error.forms.mandatory")),
            phone: Yup.string().required(t("error.forms.mandatory")),
            iban: Yup.string().required(t("error.forms.mandatory"))
                .test('valid-iban', t('error.forms.invalidIBAN'), (value) => {
                    return validateIBAN(value);
                }),
            receipt_tax_regime: Yup.string().required(t("error.forms.mandatory")),
            receipt_use_auto_invoicing: Yup.string().required(t("error.forms.mandatory")),
        })
    });

    const step4ValidationSchema = Yup.object().shape({
        type: Yup.string().required(t("error.forms.mandatory")),
    });

    const step5ValidationSchema = Yup.object().shape({
        name: Yup.string().notRequired(),
        dso_id: Yup.number().required(t("error.forms.mandatory")),
    });

    const step6ValidationSchema = Yup.object().shape({
        aggregator_deliverypoint: Yup.string().notRequired().test('isValidCPE', t("error.forms.invalidCPE"), validateCPE),
        certified_power: Yup.number().required(t("error.forms.mandatory")),
        installed_power: Yup.number().required(t("error.forms.mandatory")),
        storage_total: Yup.number().required(t("error.forms.mandatory")),
    });

    const step7ValidationSchema = Yup.object().shape({
        trading_physical_unit: Yup.number().notRequired(),
    });

    const validationSchemas = [
        step1ValidationSchema,
        step2ValidationSchema,
        step3ValidationSchema,
        step4ValidationSchema,
        step5ValidationSchema,
        step6ValidationSchema,
        step7ValidationSchema
    ];

    const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

    const handleSubmit = (values) => {
        if (activeStep < 8) {
            handleNext();
        } else {
            const _values = _.cloneDeep(values);

            _values.agent_entity = _values.agent_entity.entity.id;
            _values.business_type = "aggregation";

            delete _values.trading_physical_unit_object;

            setSubmitting(true);
            communitiesAPI.save(_values, (response) => {
                setAndShowSuccessMessage(t("success.api.create"));
                setSubmitting(false);
                setActiveStep(0);
                if (handleSuccess) {
                    handleSuccess(response.data);
                }
            }, (error) => {
                setSubmitting(false);
                setAndShowErrorMessage(error);
            });
        }
    };

    const handleOwnerRowClick = (row, formik) => {
        setSelectedOwnerEntity(row);
        formik.setFieldValue("agent_entity", row);
    };

    const handleOnChangeProducerIsAuthInvoice = (formik) => {
        formik.setFieldValue("producer_has_auto_invoice_agreement", undefined);
    }

    const fetchOwnerEntities = () => {
        setLoadingOwnerEntities(true);
        fetchAggregators([], 100, 1, false, (response) => {
            setOwnerEntities(response.data.results);
            setLoadingOwnerEntities(false);
        }, (error) => {
            console.error(error);
            setLoadingOwnerEntities(false);
        });
    }

    useEffect(() => {
        setActiveStep(0);
    }, []);

    useEffect(() => {
        fetchOwnerEntities();

        return () => {
            setOwnerEntities([]);
        }
    }, []);

    if (!ownerEntities) {
        return (
            <Skeleton variant="rectangular" width="100%" height={400} />
        )
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[activeStep]}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            onSubmit={handleSubmit}
        >
            {(formik) => (
                <Form>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        <Step key={0}>
                            <StepLabel>{t("label.menu.energyAgent")}</StepLabel>
                            <StepContent>
                                <Box maxWidth={"md"}>
                                    <Stack spacing={2}>
                                        {(ownerEntities && ownerEntities.length > 0 && !initialData) && (
                                            <IndieSimpleTable data={ownerEntities} columns={columns} loading={loadingOwnerEntities} onRowClick={(row) => {
                                                handleOwnerRowClick(row, formik);
                                            }} selected={selectedOwnerEntity} variant="outlined" />
                                        )}
                                        {(!ownerEntities || ownerEntities.length === 0) && (
                                            <Alert width={"md"} severity="warning" variant="filled">{t("error.forms.newCommunity.noEGACEntities")}</Alert>
                                        )}
                                        {initialData && (
                                            <Alert width={"md"} severity="success" variant="outlined">{`${t("newTarriffPlan.initialAgentData")} ${formik.values.agent_entity?.entity?.name}`}</Alert>
                                        )}
                                        {formik.errors.agent_entity && <FormHelperText id="agent_entity_helper_text" error>{formik.errors.agent_entity}</FormHelperText>}
                                        <Stack direction="row" spacing={2}>
                                            <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                        </Stack>
                                    </Stack>
                                </Box>
                            </StepContent>
                        </Step>
                        <Step key={1}>
                            <StepLabel>{t("newAgentCommunity.egacRegisteredDGEG")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">{t("newAgentCommunity.egacRegisteredDGEG.subtitle")}</Typography>
                                    <IndieTextField id="dgeg_name" name="dgeg_name" label={t("label.form.name")} sx={{ width: 200 }} />
                                    <IndieTextField id="dgeg_fiscal_number" name="dgeg_fiscal_number" label={t("label.form.NIF")} sx={{ width: 200 }} />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={2}>
                            <StepLabel>{t("newAgentCommunity.clientEGAC")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Stack spacing={2}>
                                        <EntityFormFields
                                            showReceiptFormFields
                                            agentEntityId={formik.values.agent_entity?.entity?.id}
                                            prefix={"egac_client"}
                                            disabled={initialData ? true : false}
                                        />
                                        <Grid2 container spacing={2}>
                                            <Grid2 item size={{ xs: 12, sm: 6, lg: 4 }}>
                                                <Typography variant="subtitle2">{t("label.title.receivingInformation")}</Typography>
                                                <Stack spacing={1}>
                                                    <IndieReceiptMethodsSelect
                                                        id="egac_client_receipt_method"
                                                        name="egac_client.receipt_method"
                                                        label={t("label.form.receiptMethod")}
                                                        disabled
                                                    />
                                                    <IndieIBANTextField
                                                        id={`egac_client_iban`}
                                                        name={`egac_client.iban`}
                                                        label={t("label.form.paymentIBAN")}
                                                        fullWidth
                                                        disabled={initialData ? true : false}
                                                    />
                                                    <SelectYesNo
                                                        id="egac_client_receipt_use_auto_invoicing"
                                                        name="egac_client.receipt_use_auto_invoicing"
                                                        label={t("label.form.isAutoInvoicingAgreement")}
                                                        onChange={() => handleOnChangeProducerIsAuthInvoice(formik)}
                                                        disabled={initialData ? true : false}
                                                    />
                                                </Stack>
                                            </Grid2>
                                        </Grid2>
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={3}>
                            <StepLabel>{t("label.step.newCommunity.communityTypeStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">{t("label.step.newAgentCommunity.communityTypeStep.subtitle")}</Typography>
                                    <CommunityTypeSelector name="type" />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={4}>
                            <StepLabel>{t("label.step.newCommunity.communityDataStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">{t("label.step.newCommunity.communityDataStep.subtitle")}</Typography>
                                    <IndieTextField id="name" name="name" label={t("label.form.name")} sx={{ width: 200 }} />

                                    {formik.values.status === "active" || formik.values.status === "suspended" ? (
                                        <IndieDesktopDatePicker id="activation_date" name="activation_date" label={t("label.form.activationDate")} sx={{ width: 200 }} />
                                    ) : null}

                                    <IndieDSOIDField id="dso_id" name="dso_id" label={t("label.form.code")} type={formik.values?.type} />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={5}>
                            <StepLabel>{t("newAgentCommunity.projectSummary")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">{t("newAgentCommunity.projectSummary.subtitle")}</Typography>
                                    <IndieTextField id="aggregator_deliverypoint" name="aggregator_deliverypoint" label={t("aggregatorDeliverypoint")} sx={{ width: 200 }} />
                                    <IndieNumberField id="certified_power" name="certified_power" label={t("label.form.certifiedPower")} sx={{ width: 200 }} />
                                    <IndieNumberField id="installed_power" name="installed_power" label={t("label.form.installedPowerKWh")} sx={{ width: 200 }} />
                                    <IndieNumberField id="storage_total" name="storage_total" label={t("label.form.storageCapacityKWhTotal")} sx={{ width: 200 }} />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={6}>
                            <StepLabel>{t("label.form.gcsCode")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <PhysicalUnitSelect
                                        id="trading_physical_unit"
                                        name="trading_physical_unit"
                                        label={t("label.form.gcsCode")}
                                        connectionPower={formik.values?.installed_power}
                                        handleOnChange={(e, v) => {
                                            formik.setFieldValue("trading_physical_unit", v?.id);
                                            formik.setFieldValue("trading_physical_unit_object", v);
                                        }}
                                        error={Boolean(formik.touched?.trading_physical_unit && formik.errors?.trading_physical_unit)}
                                        helperText={formik.touched?.trading_physical_unit && formik.errors?.trading_physical_unit}
                                        noInstallation
                                        agent={formik.values.agent_entity?.agent?.id ?? formik.values.agent_entity?.agent}
                                    />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={7}>
                            <StepLabel>{t("label.step.newCommunity.bannerUploadStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={2}>
                                    <Box maxWidth={"md"}>
                                        <NewCommunityBannerUploadStep />
                                    </Box>
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} />
                                        <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                        <Step key={8}>
                            <StepLabel>{t("label.step.newCommunity.confirmationStep")}</StepLabel>
                            <StepContent>
                                <Stack spacing={4}>
                                    <NewCommunityConfirmationStep />
                                    <Stack direction="row" spacing={2}>
                                        <IndieBackButton variant="outlined" onClick={handleBack} disabled={submitting} />
                                        <IndieSaveButton variant="contained" id="save_button" type="submit" loading={submitting} disabled={submitting} />
                                    </Stack>
                                </Stack>
                            </StepContent>
                        </Step>
                    </Stepper>
                    {/**<pre>{JSON.stringify(formik.values)}</pre>**/}
                    {/**<pre>{JSON.stringify(formik.errors)}</pre>**/}
                </Form>
            )}
        </Formik>
    );
}

const NewCommunityBannerUploadStep = () => {
    const { t } = useTranslation();

    return (
        <Fragment>
            <Stack spacing={2}>
                <Typography variant='subtitle2' color='textSecondary'> {t("label.step.newCommunity.bannerUpload.subtitle")}</Typography>
                <Divider />
                <CommunityBannerUpload id="banner_image" fieldName="banner_image" />
            </Stack>
        </Fragment>
    )
}

const NewCommunityConfirmationStep = () => {
    const { values } = useFormikContext();
    const { t } = useTranslation();

    let communityTypeDescription = "";
    try {
        communityTypeDescription = t(communityTypes.filter((t) => t.code === values.type)[0].name);
    } catch (error) {
        communityTypeDescription = "";
    }

    return (
        <Fragment>
            <Stack spacing={2}>
                <Typography variant='subtitle2' color='textSecondary'>
                    {t("label.step.newCommunity.confirmationStep.subtitle")}
                </Typography>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newTariffModel.businessType")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={t(`newAgentCommunity.confirmStep.businessType.aggregation`)} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.menu.energyAgent")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={values.agent_entity.entity.name} />
                    <IndieLabelAndValue label="label.form.NIF" value={values.agent_entity.entity.fiscal_number} />
                    <IndieLabelAndValue label="label.table.header.respName" value={values.agent_entity.resp_party?.name} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("newAgentCommunity.egacRegisteredDGEG")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={values.dgeg_name} />
                    <IndieLabelAndValue label="label.form.NIF" value={values.dgeg_fiscal_number} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("newAgentCommunity.clientEGAC")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.NIF" value={values.egac_client.fiscal_number} />
                    <IndieLabelAndValue label="Name" value={values.egac_client.name} />
                    <IndieLabelAndValue label="label.form.email" value={values.egac_client.email} />
                    <IndieLabelAndValue label="label.menu.contact" value={values.egac_client.phone} />
                    <IndieLabelAndValue label="label.table.header.address" value={values.egac_client.address} />
                    {/* <IndieLabelAndValue label="label.form.fiscalSpace" value={values.fiscal_space_object?.label} /> */}
                    <IndieLabelAndValue label="label.form.taxRegime" value={values.egac_client.receipt_tax_regime_object?.label} />
                    <IndieLabelAndValue label="label.form.hasOpenActivity" value={values.egac_client.receipt_has_open_activity ? t("label.form.yes") : t("label.form.no")} />
                    <IndieLabelAndValue label="label.form.receiptMethod" value={t("invoicesTab.method.TRF")} />
                    <IndieLabelAndValue label="label.form.paymentIBAN" value={values.egac_client.iban} />
                    <IndieLabelAndValue label="addClient.clientType.confirmationStep.autoInvoiceRegime" value={values.egac_client.receipt_use_auto_invoicing ? t("label.form.yes") : t("label.form.no")} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunity.communityDataStep")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={values.name} />
                    <IndieLabelAndValue label="label.form.code" value={`${values.dso_id} / ${_.upperCase(values.type)}`} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunity.communityTypeStep")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.communityType" value={communityTypeDescription} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("newAgentCommunity.projectSummary")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="aggregatorDeliverypoint" value={values.aggregator_deliverypoint} />
                    <IndieLabelAndValue label="label.form.certifiedPower" value={values.certified_power} />
                    <IndieLabelAndValue label="label.form.installedPowerKWh" value={values.installed_power} />
                    <IndieLabelAndValue label="label.form.storageCapacityKWhTotal" value={values.storage_total} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.form.gcsCode")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.code" value={values.trading_physical_unit_object?.name} />
                </Stack>
            </Stack>
        </Fragment>
    )
}